import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useNavigateWithParams } from '../hooks/useNavigateWithParams';
import { motion } from 'framer-motion';
import { BookOpen, List, FileQuestion, Home, Rocket, Infinity, Globe, Activity } from 'lucide-react';

import { TabNavigation } from '../components/results/TabNavigation';
import { ResultsActionBar } from '../components/results/ResultsActionBar';
import { OfferStackCard } from '../components/results/OfferStackCard';
import { ScheduleSplitLayout } from '../components/results/ScheduleSplitLayout';
import { MarketingCalendarView } from '../components/results/MarketingCalendarView';
import { parseSendWhen, sortByScheduledOrder, type ScheduleEntry } from '../lib/scheduleParsing';

interface CombinedCalendarItem {
  kind: 'email' | 'social' | 'followup';
  label: string;
  secondary: string;
  origId: string;
}
import { exportEmailsToCSV, exportSocialPostsToCSV } from '../lib/csvExporter';
import { generatePDF } from '../lib/pdfGenerator';
import { ScriptFullView } from '../components/results/ScriptFullView';
import { ScriptSectionsView } from '../components/results/ScriptSectionsView';
import { EmailCard } from '../components/results/EmailCard';
import { SocialPostCard } from '../components/results/SocialPostCard';
import { LandingPageView } from '../components/results/LandingPageView';
import { WebinarHero } from '../components/results/WebinarHero';

import {
  parseOfferStack,
  parseScriptSections,
  parseEmails,
  parseSocialPosts,
  extractFullScript,
  parseLandingPage,
  parseWebinarMetadata,
} from '../lib/contentParsers';
import {
  formatScriptForCopy,
  formatEmailsForCopy,
  formatSocialPostsForCopy,
  formatLandingPageForCopy,
} from '../lib/copyFormatters';

import type {
  WebinarResults,
  TabType,
  ScriptViewType,
} from '../types/results';
import { STORAGE_KEYS } from '../config';
import { getScopedClient } from '../lib/supabase';
import { useTenant } from '../context/TenantContext';
import { shareWebinar, makeEvergreen, getWebinars } from '../lib/webinarHistory';
import { ProductTour } from '../components/ProductTour';

export function Results() {
  const navigate = useNavigateWithParams();
  const location = useLocation();
  const { id: webinarId } = useParams<{ id: string }>();
  useTenant(); // ensures location header is set by TenantProvider
  const [activeTab, setActiveTab] = useState<TabType>('script');
  const [scriptView, setScriptView] = useState<ScriptViewType>('full');
  const [results, setResults] = useState<WebinarResults | null>(null);
  const [noResults, setNoResults] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [isEvergreen, setIsEvergreen] = useState(false);
  const [evergreenSlug, setEvergreenSlug] = useState<string | null>(null);
  const [evergreenLoading, setEvergreenLoading] = useState(false);
  const [evergreenCopied, setEvergreenCopied] = useState(false);
  const [launchStatus, setLaunchStatus] = useState<string | null>(null);

  // Schedule calendar interactivity (per-tab active + hover)
  const [activeEmailId, setActiveEmailId] = useState<string | null>(null);
  const [hoveredEmailId, setHoveredEmailId] = useState<string | null>(null);
  const [activeSocialId, setActiveSocialId] = useState<string | null>(null);
  const [hoveredSocialId, setHoveredSocialId] = useState<string | null>(null);
  const [activeFollowUpId, setActiveFollowUpId] = useState<string | null>(null);
  const [hoveredFollowUpId, setHoveredFollowUpId] = useState<string | null>(null);

  // Product tour
  const [showTour, setShowTour] = useState(false);
  const tabsRef = useRef<HTMLDivElement>(null);
  const actionBarRef = useRef<HTMLDivElement>(null);
  const launchRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (localStorage.getItem('ww_tour_completed') === 'true') return;

    async function checkFirstWebinar() {
      try {
        const webinars = await getWebinars({});
        if (webinars.length === 1) {
          setShowTour(true);
        }
      } catch {
        // Tour is non-critical
      }
    }

    checkFirstWebinar();
  }, []);

  useEffect(() => {
    // If we have a webinar ID in URL params, fetch from Supabase
    if (webinarId) {
      const fetchFromSupabase = async () => {
        try {
          const { data, error } = await getScopedClient()
            .from('webinars')
            .select('title, subtitle, webinar_script, marketing_emails, follow_up_emails, social_posts, landing_page, offer_stack, share_token, is_evergreen, evergreen_slug, launch_status')
            .eq('id', webinarId)
            .single();

          if (error || !data) {
            console.warn('Supabase fetch failed, trying localStorage fallback:', error?.message);
            loadFromLocalStorage();
            return;
          }

          const supabaseResults: WebinarResults = {
            title: data.title || '',
            subtitle: data.subtitle || '',
            webinarScript: data.webinar_script || '',
            marketingEmails: data.marketing_emails || '',
            socialPosts: data.social_posts || '',
            followUpEmails: data.follow_up_emails || '',
            landingPage: data.landing_page || '',
            offerStack: data.offer_stack || '',
          };

          // Track share token if one exists
          if (data.share_token) {
            setShareToken(data.share_token);
          }

          // Track evergreen status
          if (data.is_evergreen) {
            setIsEvergreen(true);
            setEvergreenSlug(data.evergreen_slug);
          }

          // Track launch status
          if (data.launch_status) {
            setLaunchStatus(data.launch_status);
          }

          // Also save to localStorage for refresh support
          localStorage.setItem(STORAGE_KEYS.webinarResults, JSON.stringify(supabaseResults));
          setResults(supabaseResults);
        } catch {
          console.warn('Supabase fetch error, trying localStorage fallback');
          loadFromLocalStorage();
        }
      };

      // Check route state first (from Processing page navigation)
      const stateResults = (location.state as { results?: WebinarResults } | null)?.results;
      if (stateResults) {
        setResults(stateResults);
        return;
      }

      fetchFromSupabase();
      return;
    }

    // No webinar ID — use existing behavior
    // First try route state (immediate, avoids flash on transition from Processing)
    const stateResults = (location.state as { results?: WebinarResults } | null)?.results;
    if (stateResults) {
      setResults(stateResults);
      return;
    }

    loadFromLocalStorage();
  }, [navigate, location.state, webinarId]);

  function loadFromLocalStorage() {
    // Fallback to localStorage (for direct navigation / page refresh)
    const stored = localStorage.getItem(STORAGE_KEYS.webinarResults);
    if (!stored) {
      setNoResults(true);
      return;
    }

    try {
      const parsed = JSON.parse(stored) as WebinarResults;
      if (
        !parsed.webinarScript &&
        !parsed.marketingEmails &&
        !parsed.socialPosts &&
        !parsed.followUpEmails
      ) {
        setNoResults(true);
        return;
      }
      setResults(parsed);
    } catch (error) {
      console.error('Failed to parse results:', error);
      setNoResults(true);
    }
  }

  const parsed = useMemo(() => {
    if (!results) return null;

    const marketingEmails = parseEmails(results.marketingEmails || '');
    const socialPosts = parseSocialPosts(results.socialPosts || '');
    const followUpEmails = parseEmails(results.followUpEmails || '');

    const emailSchedule = sortByScheduledOrder(
      marketingEmails.map((email, idx) => ({
        id: `email-${email.number}`,
        order: idx,
        data: email,
        slot: parseSendWhen(email.sendDay, { defaultKind: 'marketing' }),
      })),
    );

    const socialSchedule = sortByScheduledOrder(
      socialPosts.map((post, idx) => ({
        id: `social-${post.number}`,
        order: idx,
        data: post,
        slot: parseSendWhen(post.dayLabel, { defaultKind: 'social' }),
      })),
    );

    const followUpSchedule = sortByScheduledOrder(
      followUpEmails.map((email, idx) => ({
        id: `followup-${email.number}`,
        order: idx,
        data: email,
        slot: parseSendWhen(email.sendDay, { defaultKind: 'follow-up' }),
      })),
    );

    // Combined schedule for the Marketing Calendar tab
    const combinedEntries: ScheduleEntry<CombinedCalendarItem>[] = [
      ...emailSchedule.map<ScheduleEntry<CombinedCalendarItem>>((e, i) => ({
        id: `cal-email-${e.data.number}`,
        order: i,
        slot: e.slot,
        data: {
          kind: 'email',
          label: e.data.subject || `Email ${e.data.number}`,
          secondary: e.data.angle ?? e.data.sendDay ?? '',
          origId: e.id,
        },
      })),
      ...socialSchedule.map<ScheduleEntry<CombinedCalendarItem>>((s, i) => ({
        id: `cal-social-${s.data.number}`,
        order: 100 + i,
        slot: s.slot,
        data: {
          kind: 'social',
          label:
            s.data.content.split('\n')[0].slice(0, 70) +
            (s.data.content.length > 70 ? '…' : ''),
          secondary: s.data.angle ?? s.data.dayLabel ?? '',
          origId: s.id,
        },
      })),
      ...followUpSchedule.map<ScheduleEntry<CombinedCalendarItem>>((f, i) => ({
        id: `cal-followup-${f.data.number}`,
        order: 200 + i,
        slot: f.slot,
        data: {
          kind: 'followup',
          label: f.data.subject || `Follow-Up ${f.data.number}`,
          secondary: f.data.angle ?? f.data.sendDay ?? '',
          origId: f.id,
        },
      })),
    ];

    const combinedSchedule = sortByScheduledOrder(combinedEntries);

    return {
      offerStack: parseOfferStack(results.offerStack || ''),
      scriptSections: parseScriptSections(results.webinarScript || ''),
      fullScript: extractFullScript(results.webinarScript || ''),
      marketingEmails,
      socialPosts,
      followUpEmails,
      emailSchedule,
      socialSchedule,
      followUpSchedule,
      combinedSchedule,
      webinarMetadata: parseWebinarMetadata({
        title: results.title,
        subtitle: results.subtitle,
        landingPage: results.landingPage,
      }),
      landingPage: parseLandingPage(results.landingPage || ''),
    };
  }, [results]);

  // Scroll to card when user clicks a calendar row
  const scrollToCard = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleEmailCalendarClick = (id: string) => {
    setActiveEmailId(id);
    scrollToCard(id);
  };
  const handleSocialCalendarClick = (id: string) => {
    setActiveSocialId(id);
    scrollToCard(id);
  };
  const handleFollowUpCalendarClick = (id: string) => {
    setActiveFollowUpId(id);
    scrollToCard(id);
  };

  // Marketing Calendar (combined) click handler: switch tab + scroll
  const handleCombinedCalendarClick = (origId: string) => {
    if (origId.startsWith('email-')) {
      setActiveEmailId(origId);
      setActiveTab('emails');
      setTimeout(() => scrollToCard(origId), 80);
    } else if (origId.startsWith('social-')) {
      setActiveSocialId(origId);
      setActiveTab('social');
      setTimeout(() => scrollToCard(origId), 80);
    } else if (origId.startsWith('followup-')) {
      setActiveFollowUpId(origId);
      setActiveTab('followup');
      setTimeout(() => scrollToCard(origId), 80);
    }
  };

  const getCurrentTabCopy = (): { text: string; html?: string } => {
    if (!parsed) return { text: '' };
    switch (activeTab) {
      case 'script': {
        const payload = formatScriptForCopy(parsed.scriptSections, parsed.fullScript);
        return { text: payload.text, html: payload.html };
      }
      case 'emails': {
        const payload = formatEmailsForCopy(parsed.marketingEmails, 'marketing');
        return { text: payload.text, html: payload.html };
      }
      case 'social': {
        const payload = formatSocialPostsForCopy(parsed.socialPosts);
        return { text: payload.text, html: payload.html };
      }
      case 'followup': {
        const payload = formatEmailsForCopy(parsed.followUpEmails, 'followup');
        return { text: payload.text, html: payload.html };
      }
      case 'landing': {
        const payload = formatLandingPageForCopy(parsed.landingPage);
        return { text: payload.text, html: payload.html };
      }
      default:
        return { text: '' };
    }
  };

  const getCurrentTabLabel = (): string => {
    switch (activeTab) {
      case 'script':
        return 'Script';
      case 'emails':
        return 'Emails';
      case 'social':
        return 'Posts';
      case 'followup':
        return 'Follow-Up';
      case 'landing':
        return 'Landing Page';
      default:
        return '';
    }
  };

  const handleStartOver = () => {
    localStorage.removeItem(STORAGE_KEYS.webinarResults);
    localStorage.removeItem(STORAGE_KEYS.currentJobId);
    localStorage.removeItem(STORAGE_KEYS.formData);
    navigate('/');
  };

  const handleDownloadPdf = async () => {
    if (isGeneratingPdf || !results) return;
    setIsGeneratingPdf(true);
    try {
      await generatePDF(results);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleShare = async () => {
    if (shareLoading) return;

    // If already shared, just copy the existing link
    if (shareToken) {
      const url = `${window.location.origin}/script/${shareToken}`;
      await navigator.clipboard.writeText(url);
      return;
    }

    // Need a webinar ID to share
    if (!webinarId) return;

    setShareLoading(true);
    try {
      const token = await shareWebinar(webinarId);
      setShareToken(token);
      const url = `${window.location.origin}/script/${token}`;
      await navigator.clipboard.writeText(url);
    } catch (err) {
      console.error('Failed to share:', err);
    } finally {
      setShareLoading(false);
    }
  };

  const handleEvergreen = async () => {
    if (evergreenLoading) return;

    // If already evergreen, just copy the link
    if (isEvergreen && evergreenSlug) {
      const url = `${window.location.origin}/webinar/${evergreenSlug}`;
      await navigator.clipboard.writeText(url);
      setEvergreenCopied(true);
      setTimeout(() => setEvergreenCopied(false), 2500);
      return;
    }

    if (!webinarId) return;

    setEvergreenLoading(true);
    try {
      const slug = await makeEvergreen(webinarId);
      setIsEvergreen(true);
      setEvergreenSlug(slug);
      const url = `${window.location.origin}/webinar/${slug}`;
      await navigator.clipboard.writeText(url);
      setEvergreenCopied(true);
      setTimeout(() => setEvergreenCopied(false), 2500);
    } catch (err) {
      console.error('Failed to make evergreen:', err);
    } finally {
      setEvergreenLoading(false);
    }
  };

  const handleExportCsv = () => {
    if (!parsed) return;
    switch (activeTab) {
      case 'emails':
        exportEmailsToCSV(parsed.marketingEmails, 'marketing-emails.csv');
        break;
      case 'social':
        exportSocialPostsToCSV(parsed.socialPosts, 'social-posts.csv');
        break;
      case 'followup':
        exportEmailsToCSV(parsed.followUpEmails, 'follow-up-emails.csv');
        break;
    }
  };

  const handlePresenterMode = () => {
    navigate('/presenter');
  };

  if (noResults) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-6">
              <FileQuestion className="w-8 h-8 text-slate-500" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              No Webinar Found
            </h2>
            <p className="text-slate-400 mb-6">
              It looks like you haven't generated a webinar yet. Complete the questionnaire to create your personalized webinar content.
            </p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-500 text-white font-medium rounded-lg transition-colors"
            >
              <Home className="w-4 h-4" />
              Start Questionnaire
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!results || !parsed) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="w-full max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-2"
        >
          <h1 className="text-3xl font-bold text-slate-100">
            Your Webinar Content
          </h1>
          <p className="text-slate-400">
            Review, copy, and customize your AI-generated content
          </p>
          {webinarId && (
            <div className="flex items-center justify-center gap-3 mt-3 flex-wrap">
              <motion.button
                ref={launchRef as React.Ref<HTMLButtonElement>}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() =>
                  navigate(`/launch/${webinarId}`)
                }
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 hover:from-primary-500 hover:to-purple-500 text-white font-medium rounded-xl transition-all shadow-lg shadow-primary-500/25"
              >
                <Rocket className="w-4 h-4" />
                Prepare to Launch
              </motion.button>
              {launchStatus && launchStatus !== 'draft' && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.35 }}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() =>
                    navigate(`/status/${webinarId}`)
                  }
                  className="inline-flex items-center gap-2 px-5 py-3 bg-slate-800/80 hover:bg-slate-700/80 text-slate-200 border border-slate-700 font-medium rounded-xl transition-all text-sm"
                >
                  <Activity className="w-4 h-4" />
                  View Status
                </motion.button>
              )}
              <motion.button
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleEvergreen}
                disabled={evergreenLoading}
                className={`inline-flex items-center gap-2 px-5 py-3 font-medium rounded-xl transition-all text-sm ${
                  isEvergreen
                    ? evergreenCopied
                      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                      : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20'
                    : 'bg-slate-800/80 text-slate-300 border border-slate-700/50 hover:border-emerald-500/30 hover:text-emerald-400'
                }`}
              >
                {evergreenLoading ? (
                  <div className="w-4 h-4 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                ) : isEvergreen ? (
                  <Globe className="w-4 h-4" />
                ) : (
                  <Infinity className="w-4 h-4" />
                )}
                {isEvergreen
                  ? evergreenCopied
                    ? 'Link Copied!'
                    : 'Copy Evergreen Link'
                  : 'Save as Evergreen'}
              </motion.button>
            </div>
          )}
        </motion.div>

        {/* Tab Navigation + Global Actions */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="flex flex-col gap-4"
        >
          <div ref={tabsRef}>
            <TabNavigation
              activeTab={activeTab}
              onTabChange={setActiveTab}
              emailCount={parsed.marketingEmails.length}
              socialCount={parsed.socialPosts.length}
              followUpCount={parsed.followUpEmails.length}
            />
          </div>
          <div ref={actionBarRef}>
          {(() => {
            const copy = getCurrentTabCopy();
            return (
              <ResultsActionBar
                copyText={copy.text}
                copyHtml={copy.html}
                copyLabel={`Copy All ${getCurrentTabLabel()}`}
                showCopy={true}
                showPdf={true}
                onDownloadPdf={handleDownloadPdf}
                pdfLoading={isGeneratingPdf}
                showCsv={
                  activeTab === 'emails' ? parsed.marketingEmails.length > 0
                  : activeTab === 'social' ? parsed.socialPosts.length > 0
                  : activeTab === 'followup' ? parsed.followUpEmails.length > 0
                  : false
                }
                onExportCsv={handleExportCsv}
                showPresenter={activeTab === 'script'}
                onPresenterMode={handlePresenterMode}
                onStartOver={handleStartOver}
                showShare={activeTab === 'script' && !!webinarId}
                onShare={handleShare}
                isShared={!!shareToken}
                shareLoading={shareLoading}
              />
            );
          })()}
          </div>
        </motion.div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="space-y-6"
        >
          {/* SCRIPT TAB */}
          {activeTab === 'script' && (
            <>
              {parsed.webinarMetadata && (
                <WebinarHero metadata={parsed.webinarMetadata} />
              )}

              {parsed.offerStack && (
                <OfferStackCard offer={parsed.offerStack} />
              )}

              {/* View Toggle */}
              <div className="flex items-center gap-2 p-1 rounded-lg bg-slate-800/50 border border-slate-700/50 w-fit">
                <button
                  onClick={() => setScriptView('full')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    scriptView === 'full'
                      ? 'bg-primary-600/30 text-white border border-primary-500/30'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                  }`}
                >
                  <BookOpen className="w-4 h-4" />
                  Full Script
                </button>
                <button
                  onClick={() => setScriptView('sections')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    scriptView === 'sections'
                      ? 'bg-primary-600/30 text-white border border-primary-500/30'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                  }`}
                >
                  <List className="w-4 h-4" />
                  Study Sections
                </button>
              </div>

              {scriptView === 'full' ? (
                <ScriptFullView content={parsed.fullScript} />
              ) : (
                <ScriptSectionsView sections={parsed.scriptSections} />
              )}
            </>
          )}

          {/* MARKETING CALENDAR TAB */}
          {activeTab === 'calendar' && (
            <MarketingCalendarView
              combined={parsed.combinedSchedule}
              emailCount={parsed.emailSchedule.length}
              socialCount={parsed.socialSchedule.length}
              followUpCount={parsed.followUpSchedule.length}
              onItemClick={handleCombinedCalendarClick}
            />
          )}

          {/* EMAILS TAB */}
          {activeTab === 'emails' && (
            <ScheduleSplitLayout
              entries={parsed.emailSchedule as ScheduleEntry<typeof parsed.marketingEmails[number]>[]}
              activeId={activeEmailId}
              hoveredId={hoveredEmailId}
              onItemHover={setHoveredEmailId}
              onItemClick={handleEmailCalendarClick}
              emptyMessage="No marketing emails available"
              calendarTitle="Marketing Emails"
              calendarSubtitle="8 emails across the 2-week pre-webinar window"
              renderPrimary={(email) => email.subject || `Email ${email.number}`}
              renderSecondary={(email) => email.angle ?? email.sendDay}
              accentTone={() => 'emerald'}
              renderCard={(entry) => (
                <EmailCard
                  key={entry.id}
                  cardId={entry.id}
                  email={entry.data}
                  index={entry.order}
                  tone="emerald"
                  isActive={activeEmailId === entry.id || hoveredEmailId === entry.id}
                  startExpanded={activeEmailId === entry.id}
                  onHoverChange={(h) => setHoveredEmailId(h ? entry.id : null)}
                />
              )}
            />
          )}

          {/* SOCIAL TAB */}
          {activeTab === 'social' && (
            <ScheduleSplitLayout
              entries={parsed.socialSchedule as ScheduleEntry<typeof parsed.socialPosts[number]>[]}
              activeId={activeSocialId}
              hoveredId={hoveredSocialId}
              onItemHover={setHoveredSocialId}
              onItemClick={handleSocialCalendarClick}
              emptyMessage="No social posts available"
              calendarTitle="Social Posts"
              calendarSubtitle="6 posts pacing the pre-webinar conversation"
              renderPrimary={(post) => {
                const preview = post.content.split('\n')[0].slice(0, 80);
                return preview + (post.content.length > 80 ? '…' : '');
              }}
              renderSecondary={(post) => post.angle ?? post.dayLabel}
              accentTone={() => 'blue'}
              renderCard={(entry) => (
                <SocialPostCard
                  key={entry.id}
                  cardId={entry.id}
                  post={entry.data}
                  index={entry.order}
                  tone="blue"
                  isActive={activeSocialId === entry.id || hoveredSocialId === entry.id}
                  startExpanded={activeSocialId === entry.id}
                  onHoverChange={(h) => setHoveredSocialId(h ? entry.id : null)}
                />
              )}
            />
          )}

          {/* FOLLOW-UP TAB */}
          {activeTab === 'followup' && (
            <ScheduleSplitLayout
              entries={parsed.followUpSchedule as ScheduleEntry<typeof parsed.followUpEmails[number]>[]}
              activeId={activeFollowUpId}
              hoveredId={hoveredFollowUpId}
              onItemHover={setHoveredFollowUpId}
              onItemClick={handleFollowUpCalendarClick}
              emptyMessage="No follow-up emails available"
              calendarTitle="Follow-Up Emails"
              calendarSubtitle="3 emails sent after the webinar to drive replies"
              renderPrimary={(email) => email.subject || `Email ${email.number}`}
              renderSecondary={(email) => email.angle ?? email.sendDay}
              accentTone={() => 'pink'}
              showWebinarAnchor={false}
              renderCard={(entry) => (
                <EmailCard
                  key={entry.id}
                  cardId={entry.id}
                  email={entry.data}
                  index={entry.order}
                  tone="pink"
                  isActive={activeFollowUpId === entry.id || hoveredFollowUpId === entry.id}
                  startExpanded={activeFollowUpId === entry.id}
                  onHoverChange={(h) => setHoveredFollowUpId(h ? entry.id : null)}
                />
              )}
            />
          )}

          {/* LANDING PAGE TAB */}
          {activeTab === 'landing' && (
            <LandingPageView content={parsed.landingPage} />
          )}
        </motion.div>
        {showTour && (
          <ProductTour
            stops={[
              {
                targetRef: tabsRef as React.RefObject<HTMLElement>,
                text: "Here's your complete webinar package — script, emails, social posts, landing page, and your offer stack. Click each tab to explore.",
                position: 'bottom' as const,
              },
              {
                targetRef: actionBarRef as React.RefObject<HTMLElement>,
                text: "Copy content, download PDFs, export to CSV, or share your script with a link — no login needed.",
                position: 'bottom' as const,
              },
              ...(webinarId ? [{
                targetRef: launchRef as React.RefObject<HTMLElement>,
                text: "When you're ready to go live, Launch Planner walks you through scheduling and deploying everything. Your past webinars live in the Library from the home screen.",
                position: 'bottom' as const,
              }] : []),
            ]}
          />
        )}
      </div>
    </div>
  );
}
